import React from "react";
import { format, isSameMonth, isSameDay } from "date-fns";
import { CalendarEvent } from "../../utils/calendarUtils";
import {
  getCalendarDays,
  EVENT_COLORS,
  getEventsForDay,
} from "../../utils/calendarUtils";

interface MonthViewProps {
  currentDate: Date;
  events: CalendarEvent[];
  onDayClick: (date: Date) => void;
}

const DAY_HEADERS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const MonthView: React.FC<MonthViewProps> = ({
  currentDate,
  events,
  onDayClick,
}) => {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const weeks = getCalendarDays(year, month);
  const today = new Date();

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      {/* Day headers */}
      <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
        {DAY_HEADERS.map((day) => (
          <div
            key={day}
            className="px-2 py-2 text-center text-xs font-medium uppercase text-gray-500"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7">
        {weeks.map((week, wi) =>
          week.map((day, di) => {
            const isCurrentMonth = isSameMonth(day, currentDate);
            const isToday = isSameDay(day, today);
            const dayEvents = getEventsForDay(events, day);

            return (
              <button
                key={`${wi}-${di}`}
                onClick={() => onDayClick(day)}
                className={`relative min-h-[80px] border-b border-r border-gray-100 p-1.5 text-left transition-colors hover:bg-gray-50 ${
                  !isCurrentMonth ? "bg-gray-50/50" : ""
                }`}
              >
                {/* Day number */}
                <span
                  className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-sm ${
                    isToday
                      ? "bg-primary-600 font-semibold text-white"
                      : isCurrentMonth
                      ? "text-gray-900"
                      : "text-gray-300"
                  }`}
                >
                  {format(day, "d")}
                </span>

                {/* Event indicators */}
                {dayEvents.length > 0 && (
                  <div className="mt-1 space-y-0.5">
                    {dayEvents.slice(0, 2).map((event) => (
                      <div
                        key={event.id}
                        className={`truncate rounded px-1 py-0.5 text-xs ${
                          EVENT_COLORS[event.type].bg
                        } ${EVENT_COLORS[event.type].text}`}
                      >
                        {event.startTime ? `${event.startTime} ` : ""}
                        {event.title}
                      </div>
                    ))}
                    {dayEvents.length > 2 && (
                      <div className="px-1 text-xs text-gray-500">
                        +{dayEvents.length - 2} more
                      </div>
                    )}
                  </div>
                )}
              </button>
            );
          }),
        )}
      </div>
    </div>
  );
};

export default MonthView;
